import React, { useEffect, useRef, useState } from "react";
import {
  FlatList,
  Image,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import Separator from "../Separator";
import { AnimatedCircularProgress } from "react-native-circular-progress";
import { AppImages } from "../../utility/AppImages";

import { toast } from "react-toastify";
import MyGamesStatusSkeleton from "../skeleton-components/MyGamesStatusSkeleton";
import StatsWalkthrough from "../walk-through-common-components/profileTooltips/StatsWalkthrough";

interface GameSummary {
  gameTitle: string;
  gameImage: any;
  numberOfGames: number;
  isLoading?: boolean;
}

interface UserProfileData {
  playedGames?: GameSummary[];
  organised?: GameSummary[];
  challenged?: GameSummary[];
  gameLost?: GameSummary[];
  gamesWon?: GameSummary[];
}

interface MyGamesStatusProps {
  userProfileData?: UserProfileData;
  isLoading?: Boolean;
  setActiveTab?: any;
}

const MyGamesStatus: React.FC<MyGamesStatusProps> = ({
  userProfileData,
  isLoading = false,
  setActiveTab,
}) => {
  const deviceSize = useDeviceSize();
  const styles = useStyles();
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({
    bottom: 0,
    left: 0,
  });
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredItems, setFilteredItems] = useState<GameSummary[]>([]);

  const progressRefs = useRef<(TouchableOpacity | null)[]>([]);

  const calculateFill = (summary?: GameSummary[]): number => {
    if (!summary || summary.length === 0) {
      return 0;
    }
    return summary.reduce(
      (total, item) => total + (item.numberOfGames || 0),
      0
    );
  };

  const progressData = [
    {
      id: 1,
      fill: calculateFill(userProfileData?.playedGames),
      tintColor: "#7FE3F0",
      label: "Total Events",
      name: "playedGames",
      summary: userProfileData?.playedGames || [],
    },
    {
      id: 2,
      fill: calculateFill(userProfileData?.organised),
      tintColor: "#8F52B8",
      label: "Created Events",
      name: "organised",
      summary: userProfileData?.organised || [],
    },
    {
      id: 3,
      fill: calculateFill(userProfileData?.challenged),
      tintColor: "#1F87FE",
      label: "Challenged",
      name: "challenged",
      summary: userProfileData?.challenged || [],
    },
    {
      id: 4,
      fill: calculateFill(userProfileData?.gamesWon),
      tintColor: "#4CD964",
      label: "Wins",
      name: "gamesWon",
      summary: userProfileData?.gamesWon || [],
    },
    {
      id: 5,
      fill: calculateFill(userProfileData?.gameLost),
      tintColor: "#FA0A0A",
      label: "Losses",
      name: "gameLost",
      summary: userProfileData?.gameLost || [],
    },
  ];

  const handleMouseEnter = (index: number) => {
    if (progressRefs.current[index] && progressData[index].fill > 0) {
      progressRefs.current[index]?.measure(
        (x, y, width, height, pageX, pageY) => {
          setPopoverPosition({
            bottom: pageY - 370,
            left: pageX - 250,
          });
          setSelectedItem(progressData[index]);
          setFilteredItems(progressData[index].summary || []);
          setPopoverVisible(true);
        }
      );
    }
  };

  const handleMouseLeave = () => {
    setPopoverVisible(false);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (selectedItem && selectedItem.summary) {
      const filtered = selectedItem.summary.filter((item: GameSummary) =>
        item.gameTitle.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredItems(filtered);
    }
  };

  const renderDropdownItem = ({ item }: { item: GameSummary }) => (
    <View style={styles.dropdownItem}>
      <View style={{ flexDirection: "row", gap: 5 }}>
        <Image
          source={item.gameImage}
          resizeMode="contain"
          style={{ width: 32, height: 32 }}
        />
        <Text style={styles.dropdownText}>{item.gameTitle}</Text>
      </View>
      <Text style={styles.dropdownValue}>{item.numberOfGames}</Text>
    </View>
  );

  if (isLoading) {
    return <MyGamesStatusSkeleton />;
  }

  return (
    <>
      <View style={{ flex: 1 }}>
        <LinearGradient
          colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{
            flex: 1,
            padding:
              deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                ? 12
                : 24,
            borderColor: "#374151",
            borderWidth: 0.8,
            borderRadius: 8,
          }}
        >
          <StatsWalkthrough setActiveTab={setActiveTab} />

          <Separator
            color="#374151"
            height={1}
            marginTop={12}
            marginBottom={12}
            marginHorizontal={0}
          />

          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "space-between",
              ...(deviceSize === DEVICE_SIZES.XS ||
              deviceSize === DEVICE_SIZES.SM
                ? { gap: 12 }
                : {}),
            }}
          >
            {progressData.map((data, index) => (
              <TouchableOpacity
                key={data.id}
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
                ref={(el) => (progressRefs.current[index] = el)}
                style={[
                  {
                    width:
                      deviceSize === DEVICE_SIZES.XS ||
                      deviceSize === DEVICE_SIZES.SM
                        ? "48%"
                        : "18%",
                    borderRadius: 10,
                    overflow: "hidden",
                  },
                  index === 4 &&
                  (deviceSize === DEVICE_SIZES.XS ||
                    deviceSize === DEVICE_SIZES.SM)
                    ? styles.fullWidthCard
                    : null,
                ]}
              >
                <LinearGradient
                  colors={["#1F2937", "#111827"]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 0, y: 1 }}
                  style={{
                    alignItems: "center",
                    justifyContent: "center",
                    paddingVertical: 24,
                    paddingHorizontal: 16,
                  }}
                >
                  <AnimatedCircularProgress
                    rotation={0}
                    size={94}
                    width={10}
                    fill={data.fill}
                    tintColor={data.tintColor}
                    backgroundColor={`${data.tintColor}70`}
                  >
                    {(fill) => (
                      <Text
                        style={[
                          AppFontStyle.SEMIBOLD_ANYBODY_24,
                          { color: AppColors.white },
                        ]}
                      >
                        {fill}
                      </Text>
                    )}
                  </AnimatedCircularProgress>

                  <Text
                    style={[AppFontStyle.SEMIBOLD_ANYBODY_14, { marginTop: 18 }]}
                  >
                    {data.label}
                  </Text>
                </LinearGradient>
              </TouchableOpacity>
            ))}
          </View>
        </LinearGradient>
      </View>

      {popoverVisible && selectedItem && (
        <View
          style={[
            styles.popoverContainer,
            {
              maxHeight: 382,
              width:
                deviceSize === DEVICE_SIZES.SM || deviceSize === DEVICE_SIZES.XS
                  ? "90%"
                  : 416,
              bottom:
                deviceSize === DEVICE_SIZES.SM || deviceSize === DEVICE_SIZES.XS
                  ? "35%"
                  : popoverPosition.bottom,
              left:
                deviceSize === DEVICE_SIZES.SM || deviceSize === DEVICE_SIZES.XS
                  ? "5%"
                  : popoverPosition.left,
              zIndex: 9999,
              elevation: 9999,
            },
          ]}
          onMouseEnter={() => setPopoverVisible(true)}
          onMouseLeave={handleMouseLeave}
        >
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Text style={AppFontStyle.SEMIBOLD_ANYBODY_16}>
              {selectedItem.label}
            </Text>
            <Text style={AppFontStyle.SEMIBOLD_ANYBODY_16}>
              {selectedItem.fill}
            </Text>
          </View>
          <Separator
            color="#374151"
            height={1}
            marginTop={12}
            marginBottom={12}
            marginHorizontal={0}
          />

          <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
            <FlatList
              data={filteredItems}
              renderItem={renderDropdownItem}
              keyExtractor={(item, index) => index.toString()}
              showsVerticalScrollIndicator={false}
            />
          </ScrollView>
        </View>
      )}
    </>
  );
};

export default MyGamesStatus;

const useStyles = CreateResponsiveStyle(
  {
    dropdownItem: {
      flexDirection: "row",
      justifyContent: "space-between",
      paddingRight: 10,
      paddingVertical: 6,
      alignItems: "center",
    },
    dropdownText: {
      color: "white",
      fontSize: 16,
    },
    fullWidthCard: {
      width: "100%",
    },
    dropdownValue: {
      color: "white",
      fontSize: 16,
    },
    popoverContainer: {
      position: "absolute",
      width: 250,
      backgroundColor: "#0C111C",
      borderRadius: 8,
      borderWidth: 0.8,
      borderColor: "#DC2626",
      paddingLeft: 20,
      paddingRight: 20,
      paddingTop: 10,
      paddingBottom: 20,
    },
  },
  {
    [DEVICE_SIZES.MD]: {},
    [DEVICE_SIZES.LG]: {},
    [DEVICE_SIZES.SM]: {},
    [DEVICE_SIZES.XS]: {},
  }
);
